import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { CheckSquare, Circle, Clock, AlertTriangle, ChevronRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const priorityMap = {
  urgent: { label: 'Urgent', className: 'bg-red-100 text-red-700 border-red-200' },
  high:   { label: 'Haute',  className: 'bg-orange-100 text-orange-700 border-orange-200' },
  medium: { label: 'Moyen',  className: 'bg-amber-100 text-amber-700 border-amber-200' },
  low:    { label: 'Basse',  className: 'bg-slate-100 text-slate-600 border-slate-200' },
};

function relativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const dOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.round((dOnly - nowOnly) / (1000 * 60 * 60 * 24));
  if (diff < 0) return `⏱ En retard`;
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Demain';
  if (diff > 1 && diff < 7) return d.toLocaleDateString('fr-FR', { weekday: 'long' });
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export default function UpcomingTasksWidget() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scope, setScope] = useState('mine');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const isManager = user && (user.role === 'admin' || user.role === 'manager');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { limit: 6 };
        if (scope === 'mine') params.assignee_user_id = user?.user_id;
        const r = await api.get('/tasks', { params });
        // Normalize (some endpoints return {tasks:[...]}, others return an array)
        const list = Array.isArray(r.data) ? r.data : (r.data?.tasks || []);
        // Filter open only and sort by due_date asc
        const filtered = list
          .filter((t) => !['done', 'closed', 'archived'].includes(t.status))
          .sort((a, b) => (a.due_date || '9999') > (b.due_date || '9999') ? 1 : -1)
          .slice(0, 5);
        setTasks(filtered);
      } catch (e) {
        setTasks([]);
      } finally { setLoading(false); }
    })();
  }, [scope, user?.user_id]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="dash-upcoming-tasks">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
            <CheckSquare className="w-4 h-4 text-emerald-600" />
          </div>
          <h3 className="font-semibold text-slate-900 text-sm">Mes tâches en cours</h3>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {[['mine', 'Moi'], ['team', 'Équipe']].map(([v, l]) => (
                <button key={v}
                        onClick={() => setScope(v)}
                        data-testid={`dash-tasks-scope-${v}`}
                        className={`px-2.5 py-1 text-[11px] font-medium rounded-md transition-colors ${
                          scope === v ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                        }`}>{l}</button>
              ))}
            </div>
          )}
          <button
            onClick={() => navigate('/taches')}
            data-testid="dash-tasks-see-all"
            className="text-xs font-medium text-emerald-700 hover:text-emerald-900 flex items-center gap-0.5"
          >
            Voir tout <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-4 h-4 animate-spin text-emerald-500" />
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-6 text-center text-xs text-slate-500">
          <Circle className="w-6 h-6 mx-auto text-slate-300 mb-1" />
          Aucune tâche en cours.<br />
          <button
            onClick={() => navigate('/taches')}
            className="text-emerald-700 hover:text-emerald-900 font-medium mt-1"
          >
            Créer une tâche →
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="dash-tasks-list">
          {tasks.map((t) => {
            const prio = priorityMap[t.priority] || priorityMap.medium;
            const overdue = t.due_date && new Date(t.due_date) < new Date();
            return (
              <li key={t.task_id}
                  onClick={() => navigate(`/taches?open=${t.task_id}`)}
                  data-testid={`dash-tasks-item-${t.task_id}`}
                  className="py-2.5 flex items-start gap-3 cursor-pointer hover:bg-slate-50 rounded-lg -mx-2 px-2 transition-colors">
                <div className={`w-1.5 self-stretch rounded-full shrink-0 ${
                  overdue ? 'bg-red-500' : prio.className.includes('red') ? 'bg-red-500' : prio.className.includes('orange') ? 'bg-orange-400' : 'bg-slate-300'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border ${prio.className}`}>
                      {prio.label}
                    </span>
                    <span className="text-sm font-semibold text-slate-900 truncate">{t.title || t.name || 'Sans titre'}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                    {t.due_date && (
                      <span className={`flex items-center gap-1 ${overdue ? 'text-red-600 font-semibold' : ''}`}>
                        {overdue ? <AlertTriangle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                        {relativeDate(t.due_date)}
                      </span>
                    )}
                    {t.assignee_name && <span className="truncate">👤 {t.assignee_name}</span>}
                    {t.related_entity_label && (
                      <span className="text-slate-600 truncate">{t.related_entity_label}</span>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
